// src/components/posts/PostsFeed.js
import React, { useState, useEffect, useRef } from 'react';
import { reportService } from '../../services/reportService';
import { ReportStatus } from '../constants'; 
import ReportCard from '../lending/ReportCard';
import { Search, ArrowDownUp, Shield, Loader2, RefreshCw, AlertCircle } from 'lucide-react';

const ITEMS_PER_PAGE = 10;

const PostsFeed = () => {
  const [reports, setReports] = useState([]);
  const [filteredReports, setFilteredReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Pagination State
  const [visibleCount, setVisibleCount] = useState(ITEMS_PER_PAGE);
  const observerTarget = useRef(null);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [sortOrder, setSortOrder] = useState('DATE_DESC');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadReports();
  }, []);

  const loadReports = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await reportService.getPublicReports('');
      setReports(data);
    } catch (err) {
      console.error('Error loading public reports:', err);
      setError('Failed to load reports. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    filterAndSortReports();
  }, [reports, statusFilter, sortOrder, searchTerm]);

  const filterAndSortReports = () => {
    let result = [...reports];

    // 1. Filter by Search
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(r => 
        r.borrowerName?.toLowerCase().includes(term) ||
        r.borrowerMobile?.includes(searchTerm) ||
        r.id?.includes(searchTerm)
      );
    }

    // 2. Filter by Status
    if (statusFilter !== 'ALL') {
      if (statusFilter === 'DISPUTED') {
        result = result.filter(r => r.status === ReportStatus.PUBLISHED_DISPUTED);
      } else if (statusFilter === 'SETTLED') {
        result = result.filter(r => r.status === ReportStatus.PUBLISHED_SETTLED);
      } else if (statusFilter === 'UNRESOLVED') {
        result = result.filter(r => r.status === ReportStatus.PUBLISHED_UNRESOLVED);
      }
    }

    // 3. Sort
    result.sort((a, b) => {
      if (sortOrder === 'AMOUNT_DESC') {
        return (b.amount || 0) - (a.amount || 0);
      } else if (sortOrder === 'DATE_ASC') {
        const dateA = a.createdDate?.seconds || a.createdAt?.seconds || new Date(a.createdDate || a.createdAt).getTime() / 1000;
        const dateB = b.createdDate?.seconds || b.createdAt?.seconds || new Date(b.createdDate || b.createdAt).getTime() / 1000;
        return dateA - dateB;
      } else {
        // DATE_DESC (Default)
        const dateA = a.createdDate?.seconds || a.createdAt?.seconds || new Date(a.createdDate || a.createdAt).getTime() / 1000;
        const dateB = b.createdDate?.seconds || b.createdAt?.seconds || new Date(b.createdDate || b.createdAt).getTime() / 1000;
        return dateB - dateA;
      }
    });

    setFilteredReports(result);
    setVisibleCount(ITEMS_PER_PAGE); // Reset pagination when filters change
  };

  // Infinite Scroll Observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && visibleCount < filteredReports.length) {
          setVisibleCount((prev) => prev + ITEMS_PER_PAGE);
        }
      },
      { threshold: 0.1 }
    );

    if (observerTarget.current) {
      observer.observe(observerTarget.current);
    }

    return () => observer.disconnect();
  }, [visibleCount, filteredReports.length]);

  const visibleReports = filteredReports.slice(0, visibleCount);

  // Get status counts
  const statusCounts = {
    all: reports.length,
    disputed: reports.filter(r => r.status === ReportStatus.PUBLISHED_DISPUTED).length,
    unresolved: reports.filter(r => r.status === ReportStatus.PUBLISHED_UNRESOLVED).length,
    settled: reports.filter(r => r.status === ReportStatus.PUBLISHED_SETTLED).length,
  };

  if (loading) {
    return (
      <div className="min-h-[calc(100vh-64px)] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="animate-spin text-emerald-500 mx-auto mb-4" size={48} />
          <p className="text-slate-400">Loading community feed...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-12">
      
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <div>
          <h1 className="text-xl font-bold text-white flex items-center gap-2">
            <Shield className="text-emerald-500" /> Community Feed
          </h1>
          <p className="text-slate-400">Browse public reports and community activity.</p>
        </div>
        
        <button 
          onClick={loadReports}
          disabled={loading}
          className="flex items-center gap-2 bg-slate-800 hover:bg-slate-700 text-slate-300 px-4 py-2 rounded-lg font-bold transition-colors border border-slate-700 disabled:opacity-50"
        >
          <RefreshCw size={20} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400 flex items-center gap-2">
          <AlertCircle size={20} />
          {error}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 w-full mb-8">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
          <input 
            type="text" 
            placeholder="Search by name, mobile, or ID..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm focus:ring-2 focus:ring-emerald-500 outline-none"
          />
        </div>
        
        <div className="flex items-center gap-2 bg-slate-800 border border-slate-700 rounded-lg px-3 py-2">
          <ArrowDownUp size={16} className="text-slate-400" />
          <select 
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
            className="bg-transparent text-sm text-slate-300 outline-none cursor-pointer"
          >
            <option value="DATE_DESC">Newest First</option>
            <option value="DATE_ASC">Oldest First</option>
            <option value="AMOUNT_DESC">Highest Amount</option>
          </select>
        </div>
      </div>

      {/* Status Tabs */}
      <div className="flex overflow-x-auto gap-2 mb-8 pb-2 border-b border-slate-800">
        {[
          { id: 'ALL', label: 'All Reports', count: statusCounts.all },
          { id: 'DISPUTED', label: 'Active Disputes', count: statusCounts.disputed },
          { id: 'UNRESOLVED', label: 'Unresolved', count: statusCounts.unresolved },
          { id: 'SETTLED', label: 'Settled History', count: statusCounts.settled },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setStatusFilter(tab.id)}
            className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-bold transition-all flex items-center gap-2 ${
              statusFilter === tab.id 
                ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-900/30' 
                : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
            }`}
          >
            {tab.label}
            <span className={`text-xs px-1.5 py-0.5 rounded-full ${
              statusFilter === tab.id
                ? 'bg-white/20'
                : 'bg-slate-900'
            }`}>
              {tab.count}
            </span>
          </button>
        ))}
      </div>

      {/* Results Info */}
      {searchTerm && (
        <div className="mb-4 text-sm text-slate-400">
          Found <span className="text-white font-bold">{filteredReports.length}</span> result{filteredReports.length !== 1 ? 's' : ''} for "{searchTerm}"
        </div>
      )}

      {/* Grid */}
      {filteredReports.length === 0 ? (
        <div className="text-center py-20 bg-slate-900/50 rounded-xl border border-slate-800 border-dashed">
          <Shield className="text-slate-700 mx-auto mb-4" size={48} />
          <p className="text-slate-500 text-lg mb-2">No public reports found</p>
          <p className="text-slate-600 text-sm">
            {searchTerm 
              ? 'Try adjusting your search criteria' 
              : 'Reports will appear here once published'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6">
          {visibleReports.map(report => (
            <ReportCard key={report.id} report={report} isPublic={true} />
          ))}
          
          {/* Sentinel for Infinite Scroll */}
          {visibleCount < filteredReports.length && (
            <div ref={observerTarget} className="py-6 text-center text-slate-500 flex flex-col items-center">
              <Loader2 className="animate-spin text-emerald-500 mb-2" size={24} />
              <span className="text-xs">Loading more reports...</span>
            </div>
          )}

          {/* End of List Indicator */}
          {visibleCount >= filteredReports.length && filteredReports.length > 0 && (
            <div className="py-6 text-center text-slate-600 text-sm border-t border-slate-800">
              You've reached the end of the list
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PostsFeed;